import { Navigate, redirect, type LoaderFunctionArgs, type RouteObject } from 'react-router'
import { AppShell, Page, ShellSkeleton } from './components/AppShell'
import { RouteError } from './components/RouteError'
import { ButtonLink, EmptyState } from './components/ui'
import { ensure, q } from './data/queries'
import { ApiError } from './lib/api'
import { moduleLoader } from './lib/loaders'
import { syncServerTime } from './lib/utils'
import { applicationsManageRoutes, applicationsRoutes } from './modules/applications/routes'
import { documentsManageRoutes, documentsRoutes } from './modules/documents/routes'
import { canUse } from './modules/tenants'
import { trainingManageRoutes, trainingRoutes } from './modules/training/routes'
import { surveyManageRoutes, surveyRoutes } from './modules/surveys/routes'

async function shellLoader() {
  const me = await ensure(q.me)
  syncServerTime(me.serverTime)
  return null
}

async function loginLoader({ request }: LoaderFunctionArgs) {
  const next = new URL(request.url).searchParams.get('next') || '/'
  try {
    await ensure(q.me)
    return redirect(next.startsWith('/') && !next.startsWith('//') ? next : '/')
  } catch (error) {
    if (!(error instanceof ApiError)) throw error
  }
  await ensure(q.authConfig)
  return null
}

// Each portal module contributes its own routes; add new modules by appending their route arrays here.
export const routes: RouteObject[] = [
  { path: '/login', lazy: async () => ({ Component: (await import('./pages/Login')).default }), loader: loginLoader, HydrateFallback: () => null },
  { path: '/rais', lazy: async () => ({ Component: (await import('./modules/rais/RaisPage')).default }), loader: shellLoader, HydrateFallback: () => <div className="min-h-dvh bg-white" />, errorElement: <RouteError fullPage /> },
  {
    path: '/',
    element: <AppShell />,
    loader: shellLoader,
    HydrateFallback: ShellSkeleton,
    errorElement: <RouteError fullPage />,
    children: [
      {
        errorElement: <RouteError />,
        children: [
          { index: true, lazy: async () => ({ Component: (await import('./pages/Home')).default }) },
          { path: 'team', lazy: async () => ({ Component: (await import('./pages/MeetTheTeam')).default }), loader: moduleLoader('directory', () => ensure(q.team)) },
          { path: 'resources', element: <Navigate to="/" replace /> },
          ...trainingRoutes,
          ...applicationsRoutes,
          ...documentsRoutes,
          ...surveyRoutes,
          {
            path: 'manage',
            lazy: async () => ({ Component: (await import('./pages/ManageLayout')).default }),
            children: [
              ...trainingManageRoutes,
              ...applicationsManageRoutes,
              ...documentsManageRoutes,
              ...surveyManageRoutes,
              { path: 'team', lazy: async () => ({ Component: (await import('./pages/ManageTeam')).default }), loader: moduleLoader('directory', () => ensure(q.manage.team), { admin: true }) },
              { path: 'modules', lazy: async () => ({ Component: (await import('./pages/ManageModules')).default }), loader: moduleLoader(null, () => Promise.all([ensure(q.manage.modules), ensure(q.manage.tenants)]), { admin: true }) },
              { path: 'tenants', lazy: async () => ({ Component: (await import('./pages/ManageTenants')).default }), loader: moduleLoader(null, () => Promise.all([ensure(q.manage.modules), ensure(q.manage.tenants)]), { admin: true }) },
              { path: 'settings', lazy: async () => ({ Component: (await import('./pages/ManageSettings')).default }), loader: moduleLoader(null, () => Promise.all([ensure(q.manage.settings), ensure(q.manage.chat)]), { admin: true }) },
              { path: 'chat', loader: () => redirect('/manage/settings') },
            ],
          },
          {
            path: 'profile',
            lazy: async () => ({ Component: (await import('./pages/Profile')).default }),
            loader: async () => {
              const me = await ensure(q.me)
              if (canUse(me.modules, 'training')) await ensure(q.profile)
              return null
            },
          },
          // Certificates and rewards used to be top-level pages; they now live on the profile.
          { path: 'certificates', element: <Navigate to="/profile" replace /> },
          { path: 'rewards', element: <Navigate to="/profile" replace /> },
          {
            path: '*',
            element: (
              <Page>
                <EmptyState icon="404" title="Page not found" action={<ButtonLink to="/">Go to Home</ButtonLink>} />
              </Page>
            ),
          },
        ],
      },
    ],
  },
]
